import React from 'react';
import { useState, useEffect, useRef } from 'react';
import Slider from 'react-slick';
import { Modal, ModalBody } from 'reactstrap';
import Search from 'antd/es/input/Search';
import { DatePicker, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus, faQuestion } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { _danhSachHuongDanSuDung, _taoPhieuYeuCauHoTroOnline, _uploadFile, _getTenSanPham } from '../../api/baoHanh';
import { _danhSachDongSanPham, _danhSachThuongHieu } from '../../api/baoHanh';

const NGUON_TIEP_NHAN_MAC_DINH = 'Website GIGAcare';

const buildPurchaseDate = (ddMmYyyy) => {
  const [day, month, year] = (ddMmYyyy || '').split('/').map(Number);
  if (!day || !month || !year) {
    return '';
  }
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const hoTroSchema = yup.object({
  hoTenKhachHang: yup.string().required('Vui lòng nhập họ tên khách hàng'),
  soDienThoai: yup
    .string()
    .matches(/^[0-9]{10,11}$/, 'Số điện thoại không hợp lệ')
    .required('Vui lòng nhập số điện thoại'),
  tenSanPham: yup.string().required('Vui lòng nhập tên sản phẩm'),
  model: yup.string().required('Vui lòng chọn sản phẩm để tự động điền Model'),
  hang: yup.string().required('Vui lòng chọn sản phẩm để tự động điền Thương hiệu'),
  canLaySeries: yup.boolean(),
  serial: yup.string().when('canLaySeries', {
    is: true,
    then: (s) => s.required('Vui lòng nhập serial/imei cho sản phẩm này'),
  }),
  nhuCauHoTro: yup.string().required('Vui lòng chọn nhu cầu hỗ trợ'),
  noiDungCanHoTro: yup.string().required('Vui lòng nhập nội dung cần hỗ trợ'),
  ngayMuaHang: yup.string().required('Vui lòng chọn ngày mua hàng'),
  nguoiGuiYeuCau: yup.string().required('Vui lòng chọn bạn là người sử dụng sản phẩm hay đơn vị bán lẻ'),
  nguonTiepNhan: yup.string(),
});

const FormYeuCauHoTroOnline = () => {
  const [previewFiles, setPreviewFiles] = useState([]);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [submitMessage, setSubmitMessage] = useState('');
  const [ticketCode, setTicketCode] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [searchingProduct, setSearchingProduct] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(hoTroSchema),
    defaultValues: {
      hoTenKhachHang: '',
      soDienThoai: '',
      tenSanPham: '',
      maHang: '',
      model: '',
      hang: '',
      canLaySeries: false,
      serial: '',
      nhuCauHoTro: '',
      noiDungCanHoTro: '',
      ngayMuaHang: '',
      nguoiGuiYeuCau: '',
      nguonTiepNhan: NGUON_TIEP_NHAN_MAC_DINH,
    },
  });

  const tenSanPhamValue = watch('tenSanPham');
  const canLaySeries = watch('canLaySeries');
  const skipNextSearchRef = useRef(false);

  useEffect(() => {
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }

    const timer = setTimeout(async () => {
      if (!tenSanPhamValue) {
        setProductResults([]);
        return;
      }
      setSearchingProduct(true);
      try {
        const res = await _getTenSanPham(tenSanPhamValue);
        setProductResults(Array.isArray(res) ? res : []);
      } catch {
        setProductResults([]);
      } finally {
        setSearchingProduct(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [tenSanPhamValue]);

  const handleSelectSanPham = (item) => {
    skipNextSearchRef.current = true;
    setValue('tenSanPham', item.TEN_HANG ?? '', { shouldValidate: true });
    setValue('maHang', item.MA_HANG ?? '');
    setValue('model', item.MA_CHUAN ?? '', { shouldValidate: true });
    setValue('hang', item.MA_NHOM_HANG ?? '', { shouldValidate: true });
    setValue('canLaySeries', !!item.CAN_LAY_SERIES, { shouldValidate: true });
    setShowProductDropdown(false);
    setProductResults([]);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const mapped = files.map((file) => ({
      file,
      name: file.name,
      size: (file.size / 1024).toFixed(0),
      url: URL.createObjectURL(file),
      type: file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'other',
    }));
    setPreviewFiles((prev) => [...prev, ...mapped]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setPreviewFiles((prev) => {
      URL.revokeObjectURL(prev[index].url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const onSubmit = async (data) => {
    setSubmitStatus(null);
    setSubmitMessage('');

    let attachments = [];
    if (previewFiles.length) {
      const uploaded = await _uploadFile('YeuCauHoTroOnline', { files: previewFiles.map((p) => p.file) }, 'GigaCare');
      if (!uploaded) {
        setSubmitStatus('error');
        setSubmitMessage('Tải tệp đính kèm thất bại, vui lòng thử lại.');
        return;
      }
      attachments = uploaded.map((u) => u?.linkFile).filter(Boolean);
    }

    const body = {
      supportNeed: data.nhuCauHoTro,
      customerName: data.hoTenKhachHang,
      phoneNumber: data.soDienThoai,
      productName: data.tenSanPham,
      productCode: data.maHang || '',
      model: data.model || '',
      brand: data.hang || '',
      serial: data.serial || '',
      requestContent: data.noiDungCanHoTro,
      purchaseDate: buildPurchaseDate(data.ngayMuaHang),
      requesterType: data.nguoiGuiYeuCau === 'Tôi là đơn vị bán lẻ' ? 'Người bán' : 'Người dùng',
      requestSource: data.nguonTiepNhan,
      attachments,
    };

    const res = await _taoPhieuYeuCauHoTroOnline(body);
    const isSuccess = res?.success ?? res?.Success;
    const code = res?.ticketCode ?? res?.TicketCode ?? '';
    const message = res?.message ?? res?.Message;

    if (res && isSuccess) {
      setSubmitStatus('success');
      setTicketCode(code);
      setSubmitMessage(message || 'Tạo phiếu yêu cầu hỗ trợ thành công, chúng tôi sẽ liên hệ với bạn sớm nhất');
      previewFiles.forEach((p) => URL.revokeObjectURL(p.url));
      setPreviewFiles([]);
      setProductResults([]);
      reset();
    } else {
      setSubmitStatus('error');
      setTicketCode('');
      setSubmitMessage(message || 'Tạo phiếu yêu cầu thất bại, vui lòng thử lại sau.');
    }
  };

  return (
    <div className="form-ho-tro-online mb-4">
      <style>{`
                .form-ho-tro-online .htol-card {
                    background: #fff;
                    border-radius: 16px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
                    border: 1px solid #eef1f6;
                    overflow: hidden;
                }
                .form-ho-tro-online .htol-body {
                    padding: 20px 24px 24px;
                }
                .form-ho-tro-online label.form-label {
                    font-weight: 600;
                    color: #1f2d3d;
                    margin-bottom: 4px;
                    font-size: 14px;
                }
                .form-ho-tro-online .require {
                    color: #e5484d;
                }
                .form-ho-tro-online .form-control,
                .form-ho-tro-online .form-select {
                    border-radius: 10px;
                    border: 1px solid #d9dee6;
                    padding: 7px 12px;
                }
                .form-ho-tro-online .form-control:focus,
                .form-ho-tro-online .form-select:focus {
                    border-color: #1a73e8;
                    box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.15);
                }
                .form-ho-tro-online .htol-date-picker.ant-picker {
                    border-radius: 10px;
                    border: 1px solid #d9dee6;
                    padding: 7px 12px;
                }
                .form-ho-tro-online .htol-date-picker.ant-picker-focused {
                    border-color: #1a73e8;
                    box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.15);
                }
                .form-ho-tro-online .form-check {
                    margin-bottom: 0;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding-left: 0;
                }
                .form-ho-tro-online .form-check-input {
                    margin: 0;
                    cursor: pointer;
                    flex-shrink: 0;
                }
                .form-ho-tro-online .form-check-label {
                    cursor: pointer;
                    line-height: 1.2;
                }
                .form-ho-tro-online .form-check-input:checked {
                    background-color: #1a73e8;
                    border-color: #1a73e8;
                }
                .form-ho-tro-online .htol-hint {
                    font-size: 13px;
                    color: #7a8699;
                    margin-top: 4px;
                }
                .form-ho-tro-online .htol-hint-danger {
                    color: #e5484d;
                    font-weight: 600;
                }
                .form-ho-tro-online .error-text {
                    color: #e5484d;
                    font-size: 13px;
                    margin-top: 4px;
                }
                .form-ho-tro-online .htol-attach-col {
                    align-self: start;
                }
                .form-ho-tro-online .htol-khgd-inline {
                    flex: 1 1 180px;
                    min-width: 160px;
                    width: auto;
                }
                .form-ho-tro-online .htol-noidung-textarea {
                    height: 110px;
                    resize: none;
                }
                .form-ho-tro-online .htol-dropzone {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: flex-start;
                    gap: 10px;
                    border: 2px dashed #c6d0dc;
                    border-radius: 10px;
                    background: #f8fafc;
                    padding: 10px;
                    min-height: 110px;
                    transition: all 0.2s;
                }
                .form-ho-tro-online .htol-dropzone:hover {
                    border-color: #1a73e8;
                    background: #f5f9ff;
                }
                .form-ho-tro-online .file-upload-input {
                    display: none;
                }
                .form-ho-tro-online .htol-file-thumb {
                    position: relative;
                    width: 84px;
                    height: 84px;
                    flex-shrink: 0;
                    border-radius: 8px;
                    overflow: hidden;
                    background: #eef1f6;
                }
                .form-ho-tro-online .htol-file-thumb img,
                .form-ho-tro-online .htol-file-thumb video {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    display: block;
                    background: #000;
                }
                .form-ho-tro-online .htol-file-generic {
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 22px;
                    color: #7a8699;
                }
                .form-ho-tro-online .htol-file-remove {
                    position: absolute;
                    top: 2px;
                    right: 2px;
                    width: 18px;
                    height: 18px;
                    border: none;
                    border-radius: 50%;
                    background: rgba(0, 0, 0, 0.6);
                    color: #fff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    font-size: 10px;
                    padding: 0;
                }
                .form-ho-tro-online .htol-add-file {
                    width: 84px;
                    height: 84px;
                    flex-shrink: 0;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 4px;
                    border: 1.5px dashed #c6d0dc;
                    border-radius: 8px;
                    background: #fff;
                    cursor: pointer;
                    color: #5a6b82;
                    font-size: 11px;
                    text-align: center;
                    padding: 4px;
                }
                .form-ho-tro-online .htol-add-file:hover {
                    border-color: #1a73e8;
                    color: #1a73e8;
                }
                .form-ho-tro-online .htol-add-file i {
                    font-size: 16px;
                }
                .form-ho-tro-online .htol-submit-btn {
                    background: #0E71B9;
                    color: #fff;
                    border: none;
                    border-radius: 10px;
                    padding: 12px 32px;
                    font-weight: 600;
                }
                .form-ho-tro-online .htol-submit-btn:disabled {
                    opacity: 0.7;
                }
                .form-ho-tro-online .htol-product-wrap {
                    position: relative;
                }
                .form-ho-tro-online .htol-product-dropdown {
                    position: absolute;
                    z-index: 20;
                    top: calc(100% + 4px);
                    left: 0;
                    right: 0;
                    background: #fff;
                    border-radius: 10px;
                    border: 1px solid #e4e8ee;
                    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
                    max-height: 240px;
                    overflow-y: auto;
                }
                .form-ho-tro-online .htol-dropdown-item {
                    padding: 10px 14px;
                    cursor: pointer;
                    font-size: 14px;
                }
                .form-ho-tro-online .htol-dropdown-item:hover {
                    background: #f5f9ff;
                }
            `}</style>

      <h4 className="text-primary fw-bold mb-4 text-center">Tạo yêu cầu hỗ trợ online</h4>
      <div className="htol-card">
        <div className="htol-body">
          {submitStatus === 'success' && (
            <div className="alert alert-success">
              {submitMessage}
              {ticketCode && (
                <>
                  , mã phiếu: <strong>{ticketCode}</strong>
                </>
              )}
            </div>
          )}
          {submitStatus === 'error' && <div className="alert alert-danger">{submitMessage}</div>}

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label">
                  Họ tên khách hàng <span className="require">*</span>
                </label>
                <input type="text" className="form-control" {...register('hoTenKhachHang')} placeholder="Nhập họ tên khách hàng" />
                {errors.hoTenKhachHang && <div className="error-text">{errors.hoTenKhachHang.message}</div>}
              </div>

              <div className="col-md-3">
                <label className="form-label">
                  SĐT liên hệ <span className="require">*</span>
                </label>
                <input type="text" className="form-control" {...register('soDienThoai')} placeholder="Nhập số điện thoại" />
                {errors.soDienThoai && <div className="error-text">{errors.soDienThoai.message}</div>}
              </div>

              <div className="col-md-6">
                <label className="form-label">
                  Bạn là người sử dụng sản phẩm hay đơn vị bán lẻ? <span className="require">*</span>
                </label>
                <div className="d-flex align-items-center flex-wrap gap-3">
                  {['Tôi là người sử dụng sản phẩm', 'Tôi là đơn vị bán lẻ'].map((item) => (
                    <div className="form-check" key={item}>
                      <input className="form-check-input" type="radio" value={item} id={`nguoigui-${item}`} {...register('nguoiGuiYeuCau')} />
                      <label className="form-check-label" htmlFor={`nguoigui-${item}`}>
                        {item}
                      </label>
                    </div>
                  ))}
                </div>
                {errors.nguoiGuiYeuCau && <div className="error-text">{errors.nguoiGuiYeuCau.message}</div>}
              </div>

              <div className="col-md-6 htol-product-wrap">
                <label className="form-label">
                  Tên sản phẩm <span className="require">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  autoComplete="off"
                  {...register('tenSanPham', {
                    onChange: () => {
                      setValue('model', '');
                      setValue('hang', '');
                      setValue('canLaySeries', false);
                      setShowProductDropdown(true);
                    },
                  })}
                  onFocus={() => setShowProductDropdown(true)}
                  onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)}
                  placeholder="Nhập tên sản phẩm để tìm kiếm"
                />
                {errors.tenSanPham && <div className="error-text">{errors.tenSanPham.message}</div>}

                {showProductDropdown && (searchingProduct || productResults.length > 0) && (
                  <div className="htol-product-dropdown">
                    {searchingProduct ? (
                      <div className="htol-dropdown-item text-muted">Đang tìm kiếm...</div>
                    ) : (
                      productResults.map((item, index) => (
                        <div className="htol-dropdown-item" key={index} onMouseDown={() => handleSelectSanPham(item)}>
                          {item.TEN_HANG}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              <div className="col-md-3">
                <label className="form-label">
                  Model <span className="require">*</span>
                </label>
                <input type="text" className="form-control" disabled {...register('model')} placeholder="Tự động điền" />
                {errors.model && <div className="error-text">{errors.model.message}</div>}
              </div>

              <div className="col-md-3">
                <label className="form-label">
                  Thương hiệu <span className="require">*</span>
                </label>
                <input type="text" className="form-control" disabled {...register('hang')} placeholder="Tự động điền" />
                {errors.hang && <div className="error-text">{errors.hang.message}</div>}
              </div>

              <div className="col-md-6">
                <label className="form-label">
                  Ngày mua hàng <span className="require">*</span>
                </label>
                <DatePicker
                  className="form-control htol-date-picker"
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder="dd/mm/yyyy"
                  onChange={(date, dateString) => setValue('ngayMuaHang', dateString, { shouldValidate: true })}
                />
                {errors.ngayMuaHang && <div className="error-text">{errors.ngayMuaHang.message}</div>}
              </div>

              <div className="col-md-6">
                <label className="form-label">Serial/ Imei máy {canLaySeries && <span className="require">*</span>}</label>
                <input type="text" className="form-control" {...register('serial')} placeholder="Nhập số serial/imei nếu có" />
                {canLaySeries && <div className="htol-hint htol-hint-danger">Bắt buộc nhập serial/imei cho sản phẩm này</div>}
                {errors.serial && <div className="error-text">{errors.serial.message}</div>}
              </div>

              <div className="col-md-12">
                <label className="form-label">
                  Nhu cầu hỗ trợ <span className="require">*</span>
                </label>
                <div className="d-flex flex-wrap gap-4">
                  {['Tôi mới mua và chưa biết cách sử dụng', 'Máy của tôi gặp trục trặc'].map((item) => (
                    <div className="form-check" key={item}>
                      <input className="form-check-input" type="radio" value={item} id={`nhucau-${item}`} {...register('nhuCauHoTro')} />
                      <label className="form-check-label" htmlFor={`nhucau-${item}`}>
                        {item}
                      </label>
                    </div>
                  ))}
                </div>
                {errors.nhuCauHoTro && <div className="error-text">{errors.nhuCauHoTro.message}</div>}
              </div>

              <div className="col-md-6">
                <label className="form-label">
                  Nội dung cần hỗ trợ <span className="require">*</span>
                </label>
                <textarea
                  className="form-control htol-noidung-textarea"
                  {...register('noiDungCanHoTro')}
                  placeholder="Mô tả nội dung bạn cần hỗ trợ"
                />
                {errors.noiDungCanHoTro && <div className="error-text">{errors.noiDungCanHoTro.message}</div>}
              </div>

              <div className="col-md-6 htol-attach-col">
                <label className="form-label">
                  Đính kèm tệp <span className="htol-hint">(Ảnh/video giúp chúng tôi hỗ trợ bạn tốt hơn)</span>
                </label>
                <div className="htol-dropzone">
                  {previewFiles.map((file, index) => (
                    <div className="htol-file-thumb" key={index} title={`${file.name} • ${file.size} KB`}>
                      {file.type === 'image' && <img src={file.url} alt={file.name} />}
                      {file.type === 'video' && <video src={file.url} controls />}
                      {file.type === 'other' && (
                        <div className="htol-file-generic">
                          <i className="fa fa-file-alt" />
                        </div>
                      )}
                      <button type="button" className="htol-file-remove" onClick={() => removeFile(index)}>
                        <i className="fa fa-times" />
                      </button>
                    </div>
                  ))}

                  <label htmlFor="htol-file-input" className="htol-add-file">
                    <i className="fa fa-plus" />
                    <span>Thêm ảnh/ video</span>
                  </label>
                  <input
                    id="htol-file-input"
                    type="file"
                    className="file-upload-input"
                    onChange={handleFileChange}
                    multiple
                    accept="image/*,video/*"
                  />
                </div>
              </div>

              <div className="col-md-12 mt-2 text-center">
                <button type="submit" className="htol-submit-btn" disabled={isSubmitting}>
                  {isSubmitting ? 'Đang gửi...' : 'Tạo phiếu yêu cầu'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const Video = (data) => {
  const { videos } = data;
  const [listData, setListData] = useState([]);
  const [showVideo, setShowVideo] = useState(null);

  const [modalYoutube, setModalYoutube] = useState(false);

  const extractYouTubeVideoId = (url) => {
    if (!url) return null;
    try {
      // Try URL object for ?v=VIDEOID format first
      const parsedUrl = new URL(url);
      if (parsedUrl.hostname.includes('youtu.be')) {
        // youtu.be/VIDEOID
        return parsedUrl.pathname.slice(1);
      }
      if (parsedUrl.hostname.includes('youtube.com')) {
        // youtube.com/watch?v=VIDEOID
        const v = parsedUrl.searchParams.get('v');
        if (v) return v;

        // youtube.com/shorts/VIDEOID or /embed/VIDEOID
        const paths = parsedUrl.pathname.split('/');
        const idIndex = paths.findIndex((p) => ['shorts', 'embed'].includes(p));
        if (idIndex !== -1 && paths.length > idIndex + 1) {
          return paths[idIndex + 1];
        }
      }
      return null;
    } catch {
      const regex = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
      const match = url.match(regex);
      return match ? match[1] : null;
    }
  };

  const getEmbedLink = (url) => {
    const videoId = extractYouTubeVideoId(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
  };

  const getThumbnailLink = (url) => {
    const videoId = extractYouTubeVideoId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : '';
  };

  useEffect(() => {
    if (videos) {
      setListData(videos);
    }
  }, [videos]);

  const SampleNextArrow = (props) => {
    const { className, style, onClick } = props;
    return <div disabled={className?.includes('slick-disabled')} className={className} style={{ style, display: 'block' }} onClick={onClick} />;
  };

  const SamplePrevArrow = (props) => {
    const { className, style, onClick } = props;
    return <div disabled={className?.includes('slick-disabled')} className={className} style={{ style, display: 'block' }} onClick={onClick} />;
  };

  const toggleModalYoutube = (value) => {
    setModalYoutube(!modalYoutube);
    setShowVideo(value);
  };

  const rowsCount = listData.length > 10 ? 2 : 1;
  const pageSize = rowsCount * 5;

  return (
    <div className="list-slide-video">
      <Slider
        rows={rowsCount}
        slidesPerRow={1}
        slidesToShow={5}
        infinite={listData.length > pageSize}
        className="my-3"
        autoplay={false}
        loop={true}
        slidesToScroll={2}
        nextArrow={<SampleNextArrow />}
        prevArrow={<SamplePrevArrow />}
        responsive={[
          {
            breakpoint: 991,
            settings: {
              slidesToShow: listData == undefined || listData.length >= 3 ? 3 : listData.length,
              slidesToScroll: 1,
            },
          },
          {
            breakpoint: 767,
            settings: {
              slidesToShow: listData == undefined || listData.length >= 2 ? 2 : listData.length,
              slidesToScroll: 1,
            },
          },
          {
            breakpoint: 480,
            settings: {
              slidesToShow: listData == undefined || listData.length >= 1 ? 1 : listData.length,
              slidesToScroll: 1,
            },
          },
        ]}
      >
        {listData.map((item, index) => (
          <div key={index} className="video-slider position-relative">
            <div onClick={() => toggleModalYoutube(item)}>
              <div className="video-thumbnail-wrapper" style={{ cursor: 'pointer' }}>
                <img src={getThumbnailLink(item.LinkVideo)} alt={item.TieuDe} style={{ width: '100%', height: 'auto', display: 'block' }} />
                <div className="youtube-play-overlay" title="PLay video">
                  <svg className="youtube-play-icon" viewBox="0 0 68 48" width="68" height="48">
                    <path
                      d="M66.52 7.85a8.27 8.27 0 0 0-5.82-5.83C56.59 1.2 34 1.2 34 1.2s-22.59 0-26.7.82a8.27 8.27 0 0 0-5.82 5.83C0.66 12 0.66 24 0.66 24s0 12 0.82 16.15a8.27 8.27 0 0 0 5.82 5.83c4.11.82 26.7.82 26.7.82s22.59 0 26.7-.82a8.27 8.27 0 0 0 5.82-5.83c0.82-4.11 0.82-16.15 0.82-16.15s0-12-0.82-16.15z"
                      fill="#f00"
                    />
                    <path d="M26.6 33.6v-19.2l17.1 9.6-17.1 9.6z" fill="#fff" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="content-list-video mt-1" style={{ cursor: 'pointer' }}>
              <h5 className="fw-bold tieude">
                <div onClick={() => toggleModalYoutube(item)}>{item.TieuDe}</div>
              </h5>
            </div>
          </div>
        ))}
      </Slider>
      {modalYoutube && (
        <Modal isOpen={modalYoutube} toggle={toggleModalYoutube} size="lg" className="modal-video-slider">
          <ModalBody>
            <p className="modal-video-youtube">
              <iframe
                width="100%"
                src={getEmbedLink(showVideo.LinkVideo)}
                title={showVideo.TieuDe}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </p>
          </ModalBody>
        </Modal>
      )}
    </div>
  );
};

const BaiViet = ({ baiviet }) => {
  const [listData, setListData] = useState([]);

  useEffect(() => {
    if (baiviet) {
      setListData(baiviet);
    }
  }, [baiviet]);

  const SampleNextArrow = (props) => {
    const { className, style, onClick } = props;
    return <div disabled={className?.includes('slick-disabled')} className={className} style={{ style, display: 'block' }} onClick={onClick} />;
  };

  const SamplePrevArrow = (props) => {
    const { className, style, onClick } = props;
    return <div disabled={className?.includes('slick-disabled')} className={className} style={{ style, display: 'block' }} onClick={onClick} />;
  };

  const rowsCount = listData.length > 8 ? 3 : 1;
  const pageSize = rowsCount * 4;

  return (
    <Slider
      rows={rowsCount}
      slidesPerRow={1}
      slidesToShow={4}
      infinite={listData.length > pageSize}
      className="my-3"
      autoplay={false}
      loop={true}
      slidesToScroll={2}
      nextArrow={<SampleNextArrow />}
      prevArrow={<SamplePrevArrow />}
      responsive={[
        {
          breakpoint: 991,
          settings: {
            slidesToShow: baiviet == undefined || baiviet.length >= 3 ? 3 : baiviet.length,
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 767,
          settings: {
            slidesToShow: baiviet == undefined || baiviet.length >= 2 ? 2 : baiviet.length,
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 480,
          settings: {
            slidesToShow: baiviet == undefined || baiviet.length >= 1 ? 1 : baiviet.length,
            slidesToScroll: 1,
          },
        },
      ]}
    >
      {baiviet?.map((item, index) => (
        <div key={index} className="content-news-item">
          <a href={item.LinkBaiViet} target="_blank" rel="noopener noreferrer">
            <div className="item-thumb">
              <img
                src={
                  item?.Thumbnail && item?.Id
                    ? `http://10.10.11.12/files/CmsHuongDanSuDung/BaiViet/${item.Id}/${item.Thumbnail}`
                    : '/Logo-GigaDigital.png'
                }
                alt={item.TieuDe}
                className="rounded"
              />
            </div>
            <div className="item-content mt-2">
              <h5>{item.TieuDe}</h5>
            </div>
          </a>
        </div>
      ))}
    </Slider>
  );
};

const Cauhoi = ({ questions }) => {
  const [listData, setListData] = useState([]);
  const [expanded, setExpanded] = useState(false); // track expand/collapse

  useEffect(() => {
    if (questions) {
      setListData(questions);
    }
  }, [questions]);

  const ldJson = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: listData.map((item) => ({
      '@type': 'Question',
      name: item.TieuDe,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.CauTraLoiNgan,
      },
    })),
  };

  const Item = ({ item }) => {
    const [showAnswer, setShowAnswer] = useState(true);

    const Linkify = ({ text, pattern, formatter }) => {
      const __html = text.replace(pattern, formatter);
      return <div dangerouslySetInnerHTML={{ __html }} />;
    };

    const text = item.CauTraLoiNgan;
    const pattern =
      /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi;
    const formatter = (str) => `<a href="${str}" style="color:#3590de" target="_blank">${str}</a>`;

    return (
      <div className="card-question">
        <div className="card-question-item position-relative">
          <div className="d-flex align-items-center justify-content-between mb-2 cursor-pointer" onClick={() => setShowAnswer(!showAnswer)}>
            <div className="question-icon d-flex align-items-center">
              <div className="item-icon">
                <FontAwesomeIcon icon={faQuestion} className="icon" />
              </div>
              <h6 className="mb-0">
                <b>{item.TieuDe}</b>
              </h6>
            </div>
            <div className="button-show-answer">{showAnswer ? <FontAwesomeIcon icon={faPlus} /> : <FontAwesomeIcon icon={faMinus} />}</div>
          </div>
          {!showAnswer && (
            <div className="list-answer">
              <p>
                <Linkify text={text} pattern={pattern} formatter={formatter} />
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const visibleItems = expanded ? listData : listData.slice(0, 3);

  return (
    <div className="frequently-questions mt-3">
      {listData.length > 0 && (
        <>
          {visibleItems.map((item, index) => (
            <Item item={item} key={index} />
          ))}

          {listData.length > 3 && (
            <div className="mt-2 text-center">
              <button className="btn btn-link" onClick={() => setExpanded(!expanded)}>
                {expanded ? 'Ẩn bớt' : 'Xem thêm'}
              </button>
            </div>
          )}

          <script type="application/ld+json">{JSON.stringify(ldJson)}</script>
        </>
      )}
    </div>
  );
};

const HuongDanSuDung = () => {
  const [maHang, setMaHang] = useState('');
  const [tukhoa, setTuKhoa] = useState('');
  const [hdsd, setHdsd] = useState(null);
  const [phanLoai, setPhanLoai] = useState('');
  const [thuonghieu, setThuongHieu] = useState('');
  const [loaiphukien, setLoaiPhuKien] = useState([]);
  const [IdSanPhamTuongThich, setIdSanPhamTuongThich] = useState([]);
  const [danhSachDongSanPhamEcovacs, setDanhSachDongSanPhamEcovacs] = useState([]);
  const [danhSachThuongHieuEcovacs, setDanhSachThuongHieuEcovacs] = useState([]);
  const [sptt, setSPTT] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [showAllMenu, setShowAllMenu] = useState(false);
  const [showMegaThuongHieu, setShowMegaThuongHieu] = useState(false);
  const [showMegaCategory, setShowMegaCategory] = useState(false);
  const [loadingHdsd, setLoadingHdsd] = useState(false);
  const [loadingThuongHieu, setLoadingThuongHieu] = useState(false);

  const loadHuongDanSuDung = async (params = {}) => {
    setLoadingHdsd(true);
    try {
      const data = await _danhSachHuongDanSuDung(
        params.phanLoai ?? phanLoai,
        params.thuonghieu ?? thuonghieu,
        params.sptt ?? IdSanPhamTuongThich,
        params.tukhoa ?? tukhoa,
      );

      setHdsd(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingHdsd(false);
    }
  };
  useEffect(() => {
    const delay = setTimeout(() => {
      loadHuongDanSuDung();
    }, 300);

    return () => clearTimeout(delay);
  }, [thuonghieu, phanLoai, IdSanPhamTuongThich, tukhoa]);

  const chonPhanLoai = (data) => {
    setPhanLoai(data);
  };

  useEffect(() => {
    const onLoadData = async () => {
      setLoadingThuongHieu(true);
      try {
        const [danhSachThuongHieu] = await Promise.all([_danhSachThuongHieu()]);
        setDanhSachThuongHieuEcovacs(danhSachThuongHieu);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoadingThuongHieu(false);
      }
    };
    onLoadData();
  }, []);

  const getThuongHieu = async (thuonghieu) => {
    setThuongHieu(thuonghieu);
    setLoaiPhuKien([]);
    setSPTT('');
    setShowMegaThuongHieu(false);
    _danhSachDongSanPham(thuonghieu).then((res) => {
      setDanhSachDongSanPhamEcovacs(res);
    });
  };

  const searchSanPham = (id, name) => {
    setIdSanPhamTuongThich(id);
    setSPTT(name);
  };

  const removeThuongHieu = () => {
    setThuongHieu('');
    setLoaiPhuKien([]);
    setSPTT('');
    setIdSanPhamTuongThich(null);
  };

  const removeSPTT = () => {
    setSPTT('');
    setIdSanPhamTuongThich(null);
  };

  const removeTuKhoa = () => {
    setTuKhoa('');
  };

  return (
    <div className="bg-light container rounded py-4">
      <FormYeuCauHoTroOnline />

      <h4 className="text-primary fw-bold mb-4 text-center">Hỗ trợ về sản phẩm</h4>

      <div className="card timkiem_filter mt-3">
        <div className="card-header d-block mb-0">
          <div className="filter_product m-3">
            <div className="list-search-category list-thuonghieu" style={{ display: 'flex' }} onClick={() => setShowMegaThuongHieu((prev) => !prev)}>
              <div className="text-primary" style={{ marginRight: '31px' }}>
                Tìm kiếm:
              </div>
              Thương hiệu&nbsp;
              <span>
                :<span className="text-primary"> {thuonghieu} </span>
              </span>
              &nbsp;
              <i className="fa fa-caret-down cursor-pointer" />
              <div className="row">
                <div
                  className={`w-100 mega-thuonghieu col-md-12 phukien_filter rounded p-3 ${!thuonghieu ? 'hoverable' : ''} ${showMegaThuongHieu && thuonghieu ? 'clicked' : ''}`}
                >
                  {loadingThuongHieu && (
                    <div style={{ width: '100%', textAlign: 'center', padding: '16px 0' }}>
                      <Spin indicator={<LoadingOutlined spin />} />
                    </div>
                  )}
                  {!loadingThuongHieu &&
                    danhSachThuongHieuEcovacs &&
                    (showAll
                      ? danhSachThuongHieuEcovacs.filter((x) => x.CmsSttHienWeb)
                      : danhSachThuongHieuEcovacs.filter((x) => x.CmsSttHienWeb).slice(0, 11)
                    ).map((item, index) => (
                      <div key={index} style={{ width: '100%' }} className="col-md-2 tab_thuonghieu">
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            getThuongHieu(item.MaNhomHangChiTiet);
                            setShowMegaThuongHieu(false);
                          }}
                          className={`sub-label fw-bold text-primary tab_thuonghieu img cursor-pointer ${thuonghieu === item.MaNhomHangChiTiet ? 'active' : ''}`}
                        >
                          <img src={item.CmsImageLink} alt={item.TenThuongHieu || 'Brand'} />
                        </div>
                      </div>
                    ))}

                  {!showAll && danhSachThuongHieuEcovacs?.length > 11 && (
                    <div
                      style={{ width: '100%' }}
                      className="col-md-2 tab_thuonghieu d-flex align-items-center justify-content-center"
                      onClick={() => setShowAll(true)}
                      title="Xem thêm thương hiệu khác"
                    >
                      <div className="sub-label fw-bold text-primary fs-3 cursor-pointer">…</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div
              className="list-category"
              tabIndex="0"
              style={{ marginBottom: '6px' }}
              onClick={() => {
                if (sptt) {
                  setShowMegaCategory((prev) => !prev);
                }
              }}
            >
              Sản phẩm tương thích&nbsp;
              <span>
                : <span className="text-primary">{sptt}</span>
              </span>
              &nbsp;
              <i className="fa fa-caret-down" />
              <div className={`w-100 mega-category rounded p-3 ${!sptt ? 'hoverable' : ''} ${showMegaCategory && sptt ? 'clicked' : ''}`}>
                <div className="row">
                  {danhSachDongSanPhamEcovacs &&
                    danhSachDongSanPhamEcovacs
                      .sort((a, b) => a.SttHienThi - b.SttHienThi)
                      .map((item, index) => (
                        <div key={index} className={item.length > 0 ? 'col-md-3 list' : 'col-md-3 only'}>
                          <div className="col-md-12 li">
                            <div className="sub-label fw-bold text-primary cursor-pointer">{item.TenDongSanPham}</div>
                          </div>
                          <div className="row" key={index}>
                            {item.SanPhamTuongThich &&
                              item.SanPhamTuongThich.length > 0 &&
                              item.SanPhamTuongThich.sort((a, b) => a.STT_HIEN_THI - b.STT_HIEN_THI).map((i, index) => (
                                <div
                                  className="sub-child-label cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    searchSanPham(i.ID, i.TEN_SAN_PHAM_TUONG_THICH);
                                    setShowMegaCategory(false);
                                  }}
                                  key={index}
                                >
                                  {i.TEN_SAN_PHAM_TUONG_THICH}
                                </div>
                              ))}
                          </div>
                        </div>
                      ))}
                </div>
              </div>
            </div>
            <div className="list-search-category" style={{ width: '40%' }}>
              <Search
                placeholder="Gõ từ khóa muốn tìm"
                enterButton="Tìm kiếm"
                size="large"
                onSearch={(value) => setTuKhoa(value)}
                onChange={(e) => setTuKhoa(e.target.value)}
              />
            </div>
          </div>
        </div>
        {(thuonghieu || sptt || tukhoa) && (
          <div className="card-body">
            <div className="d-flex align-items-center p-2">
              <span>Đã chọn:</span>
              {thuonghieu && (
                <span className="badge bg-primary ms-2 cursor-pointer p-2" style={{ fontSize: '13px' }} onClick={removeThuongHieu}>
                  {thuonghieu} <i className="fa fa-times ms-1"></i>
                </span>
              )}

              {sptt && (
                <span className="badge bg-primary ms-2 cursor-pointer p-2" style={{ fontSize: '13px' }} onClick={removeSPTT}>
                  {sptt} <i className="fa fa-times ms-1"></i>
                </span>
              )}

              {tukhoa && (
                <span className="badge bg-primary ms-2 cursor-pointer p-2" style={{ fontSize: '13px' }} onClick={removeTuKhoa}>
                  {tukhoa} <i className="fa fa-times ms-1"></i>
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {!thuonghieu && (
        <div className="res_pc">
          <div className="fw-bold text-primary my-4 text-center" style={{ fontSize: '21px' }}>
            Bạn đang tìm kiếm phụ kiện của thương hiệu nào?
          </div>
          <div className="col-md-12 phukien_filter mt-3">
            {danhSachThuongHieuEcovacs &&
              danhSachThuongHieuEcovacs
                .filter((x) => x.CmsSttHienWeb)
                .slice(0, showAllMenu ? undefined : 11)
                .map((item, index) => (
                  <div
                    key={index}
                    onClick={() => getThuongHieu(item.MaNhomHangChiTiet)}
                    className="col-md-2 tab_thuonghieu d-flex align-items-center justify-content-center mb-3"
                  >
                    <img src={item.CmsImageLink} alt={item.TenThuongHieu} />
                  </div>
                ))}

            {!showAllMenu && danhSachThuongHieuEcovacs?.length > 11 && (
              <div
                className="col-md-2 tab_thuonghieu d-flex align-items-center justify-content-center mb-3"
                onClick={() => setShowAllMenu(true)}
                style={{ cursor: 'pointer' }}
                title="Xem thêm thương hiệu khác"
              >
                <span className="fw-bold text-primary">Xem thêm …</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card mt-4">
        <div className="card-header">
          <div className="d-flex mb-3 mt-3 flex-wrap gap-3">
            {['Hướng dẫn sử dụng', 'Mẹo nhỏ & tính năng hay', 'Khắc phục lỗi thường gặp', 'Trải nghiệm sản phẩm'].map((label) => (
              <button
                key={label}
                className={`btn ${phanLoai === label ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => chonPhanLoai(label)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="card-body" style={{ position: 'relative', minHeight: loadingHdsd ? '160px' : 'auto' }}>
          {loadingHdsd && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 28 }} spin />} tip="Đang tải dữ liệu hỗ trợ sản phẩm..." />
            </div>
          )}
          <div className="list-items-video" style={{ display: loadingHdsd ? 'none' : 'block' }}>
            {hdsd?.Video && (
              <div className="items">
                <h5>Video</h5>
                <smal>
                  <i>
                    Đã tìm thấy <b>{hdsd.Video?.length}</b> kết quả
                  </i>
                </smal>
                <Video videos={hdsd.Video}></Video>
              </div>
            )}
            {hdsd?.BaiViet && (
              <div className="items">
                <h5>Bài viết</h5>
                <smal>
                  <i>
                    Đã tìm thấy <b>{hdsd.BaiViet?.length}</b> kết quả
                  </i>
                </smal>
                <BaiViet baiviet={hdsd.BaiViet}></BaiViet>
              </div>
            )}
            {hdsd?.CauHoiNgan && (
              <div className="items">
                <h5>Câu hỏi thường gặp</h5>
                <smal>
                  <i>
                    Đã tìm thấy <b>{hdsd.CauHoiNgan?.length}</b> kết quả
                  </i>
                </smal>
                <Cauhoi questions={hdsd.CauHoiNgan}></Cauhoi>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HuongDanSuDung;
